import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Text, TextButton } from '@zillow/constellation-10';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import numberUtils from '../../../../utils/numberUtils';
import { default as zrmPropertyUtils } from '../../../../utils/propertyUtils';
import accessUtils from '../../../../utils/accessUtils';
import {
  getGenderPreferencesDisplay,
  getRoomCurrentPetsDisplay,
  getRoommateCountDisplay,
} from '../../../../containers/CreateListingPage/utils';

import { ROOM_FOR_RENT_FLOW } from '../../../../containers/CreateListingPage/constants';

import { MODAL_COMPONENT, PET_POLICY } from '../constants';
import { TRIGGER_LOCATIONS } from '../../../../constants/ClickstreamConstants';
import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';
import RecommendationIconBulb from '../../../RecommendationIconBulb';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RMWPopover from '../../../RMWPopover';
import InfoPopover from '../../SpecialOffers/components/InfoPopover';
import SpecialOfferContainer from '../../SpecialOffers/components/SpecialOfferContainer';

import AccordionIconError from './AccordionIconError';
import ContentSectionEditButton from './ContentSectionEditButton';
import FieldValueDisplay from './FieldValueDisplay';
import { baseRentPopoverContents } from './PropertyCard';
import { StyledIconPlaceholder, StyledReviewSection, StyledReviewSectionTitle, StyledReviewTitleRow } from '../styles';

const { RENT_DETAILS } = REVIEW_PAGE_SECTION_RECS;

const ariaLabels = {
  MONTHLY_RENT: 'monthly-rent',
  UTILITIES: 'utilities',
  SECURITY_DEPOSIT: 'security-deposit',
  DATE_AVAILABLE: 'date-available',
  RENTERS_INSURANCE: 'renters-insurance',
  ROOMMATE_COUNT: 'roommate-count',
  ROOMMATE_DESCRIPTION: 'roommate-description',
  RENTER_AGE_AND_GENDER_PREFERRED: 'renter-age-and-gender-preferred',
  PETS_CURRENTLY_ON_THE_PROPERTY: 'pets-currently-on-the-property',
  LEASE_TERMS: 'lease-terms',
  LEASE_DURATION: 'lease-duration',
  SPECIAL_OFFERS: 'special-offers',
};

const propTypes = {
  onEditClick: PropTypes.func.isRequired,
};

const defaultProps = {};

const RentDetailsContent = ({ onEditClick }) => {
  const {
    modularListingState: { canAccessScreeningCriteriaInListingFlow, roomForRentFlow },
  } = useContext(ModularListingContext);

  const {
    app: { appConfig, canAccessRentalProtectionFromML, cookieEnabledServices },
    user,
    user: {
      features: { canUpdateListing },
    },
    properties: {
      recommendations,
      currentProperty,
      currentProperty: {
        petsAllowed,
        active,
        isFeed,
        isArchived,
        restrictions: { isUpdateListingRestricted },
        floorplans: [{ price = 0, roomForRent: { monthlyUtilitiesShareDescription = '' } = {}, securityDeposit } = {}],
        rentalProtection: { isEnabled: isRentalProtectionEnabled },
        roomForRent: {
          currentPets,
          currentRoommatesDetails,
          currentRoommatesDetails: { roommateDetailsDescription } = {},
          roommateRequirements: { roommateGenderPreferences, roommateMinAgePreference } = {},
        } = {},
        specialOffers,
        totalMonthlyPriceWithFeeNotice: { isEligible: isTotalMonthlyPriceWithFeeEligible } = {},
      },
    },
  } = useSelector((state) => state);

  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const customNoneFieldValueText = isFeed ? 'None' : 'Action needed';

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const isPetsAllowedEmpty = petsAllowed && zrmPropertyUtils.getIsPetsAllowedEmpty(petsAllowed);

  const displayPrice = `${numberUtils.asCurrency(price)} /month`;
  const displaySecurityDeposit = numberUtils.asCurrency(securityDeposit);

  const isDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  const displayPetsAvailable = useMemo(() => {
    if (!petsAllowed || canAccessScreeningCriteriaInListingFlow) {
      return;
    }

    const relevantEntries = Object.entries(petsAllowed).filter(([key]) => {
      // exclude these keys
      return !['cats', 'dogs', 'largeDogs'].includes(key);
    });

    const foundArr = relevantEntries
      .map(([key, value]) => {
        const { allowed = false } = value;

        return allowed ? PET_POLICY[key] : null;
      })
      // removes null values
      .filter(Boolean);

    if (foundArr.length === 0 && petsAllowed?.none === true) {
      return 'No pets allowed';
    }

    return foundArr.join(', ');
  }, [canAccessScreeningCriteriaInListingFlow, petsAllowed]);

  const displayPetsHeading = isRoomForRent ? 'Pets allowed' : 'Pets';
  const ariaLabelDisplayPets = useMemo(() => displayPetsHeading.toLowerCase().split().join('-'), [displayPetsHeading]);

  const isRyanFlow = roomForRentFlow === ROOM_FOR_RENT_FLOW.RYAN_FLOW;

  const roommateCountComponentKey =
    roomForRentFlow === ROOM_FOR_RENT_FLOW.IRENE_FLOW
      ? MODAL_COMPONENT.TotalRoommateCountFormSectionContainer.componentKey
      : MODAL_COMPONENT.DetailedRoommateCountFormSectionContainer.componentKey;

  const displayRoommateCount = getRoommateCountDisplay({ currentRoommatesDetails });

  const genderPreferred = getGenderPreferencesDisplay(roommateGenderPreferences);
  const displayRenterAgeAndGenderPreferred =
    roommateMinAgePreference === null ? `${genderPreferred}` : `${roommateMinAgePreference}, ${genderPreferred}`;

  const displayRoomCurrentPets = getRoomCurrentPetsDisplay({ currentPets });

  const displaySpecialOffers = useMemo(() => {
    if (specialOffers.length === 0) {
      return null;
    } else {
      return <SpecialOfferContainer />;
    }
  }, [specialOffers]);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const canAccessUpdatedTotalMonthlyPriceCopy = accessUtils.canAccessUpdatedTotalMonthlyPriceCopy({
    appConfig,
    cookieEnabledServices,
  });

  return (
    <div data-testid="RentDetailsContent" data-group-title="RentDetails">
      <StyledReviewSection data-testid="MonthlyRent">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {!price && !isDisabled && !isFeed && (
                <AccordionIconError tabIndex={0} title="Monthly rent incomplete error" marginRight="xs" />
              )}
              {recommendations[RENT_DETAILS][RECS.PRICE] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.MONTHLY_RENT}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.PRICE);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">{canAccessTotalMonthlyPriceWithFee ? 'Base' : 'Monthly'} rent</Text>{' '}
              {canAccessTotalMonthlyPriceWithFee && (
                <RMWPopover
                  {...baseRentPopoverContents({
                    canAccessUpdatedTotalMonthlyPriceCopy,
                    currentProperty,
                    user,
                    triggerObject: 'posting_path_review',
                    triggerLocation: 'zrm_review_rent_details_section',
                  })}
                />
              )}
              {recommendations[RENT_DETAILS][RECS.PRICE] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.MONTHLY_RENT}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.PRICE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.MONTHLY_RENT}
            value={
              canAccessTotalMonthlyPriceWithFee
                ? `${MODAL_COMPONENT.MonthlyRentFormField.componentKey}_TMP`
                : MODAL_COMPONENT.MonthlyRentFormField.componentKey
            }
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={price ? displayPrice : ''} customNoneText={customNoneFieldValueText} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="SpecialOffers">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Special offer</Text>
            <InfoPopover
              currentProperty={currentProperty}
              user={user}
              triggerLocation={TRIGGER_LOCATIONS.POSTING_PATH_REVIEW}
            />
          </Box>
          <ContentSectionEditButton
            buttonFieldName={ariaLabels.SPECIAL_OFFERS}
            value={MODAL_COMPONENT.SpecialOfferFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        {/* TODO will need custom component for special offers bc it is requires extra formatting and delete button */}
        <FieldValueDisplay customDisplayComponent={displaySpecialOffers} value="" />
      </StyledReviewSection>

      {isRoomForRent && (
        <StyledReviewSection data-testid="RoomUtilitiesAmount">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Utilities</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.UTILITIES}
              value={MODAL_COMPONENT.RoomUtilitiesAmountFormField.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={monthlyUtilitiesShareDescription} />
        </StyledReviewSection>
      )}

      <StyledReviewSection data-testid="SecurityDeposit">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {recommendations[RENT_DETAILS][RECS.SECURITY_DEPOSIT] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.SECURITY_DEPOSIT}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.SECURITY_DEPOSIT);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Security deposit</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.SECURITY_DEPOSIT}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(RECS.SECURITY_DEPOSIT);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.SECURITY_DEPOSIT}
            value={MODAL_COMPONENT.SecurityDepositFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay
          value={securityDeposit !== undefined && securityDeposit !== '' ? displaySecurityDeposit : null}
        />
        {canAccessRentalProtectionFromML && isRentalProtectionEnabled && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-xs" asChild>
              <p>You entered this amount while starting a request for Rental Protection.</p>
            </Text>
          </Box>
        )}
      </StyledReviewSection>

      {isRoomForRent && (
        <StyledReviewSection data-testid="RoommateCount">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Roommate count</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ROOMMATE_COUNT}
              value={roommateCountComponentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={displayRoommateCount} />
        </StyledReviewSection>
      )}

      {isRoomForRent && isRyanFlow && (
        <StyledReviewSection data-testid="RoommateDescription">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Roommate(s) description</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ROOMMATE_DESCRIPTION}
              value={MODAL_COMPONENT.RoommateDescriptionFormField.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={roommateDetailsDescription} />
        </StyledReviewSection>
      )}

      {isRoomForRent && isRyanFlow && (
        <StyledReviewSection data-testid="RenterAgeAndGenderPreference">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Renter gender preferred</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.RENTER_AGE_AND_GENDER_PREFERRED}
              value={MODAL_COMPONENT.GenderPreferenceFormField.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={displayRenterAgeAndGenderPreferred} />
        </StyledReviewSection>
      )}

      {isRoomForRent && (
        <StyledReviewSection data-testid="RoomCurrentPets">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder />
              <Text textStyle="body-bold">Pets currently on the property</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.PETS_CURRENTLY_ON_THE_PROPERTY}
              value={MODAL_COMPONENT.RoomCurrentPetsFormSectionContainer.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={displayRoomCurrentPets} />
        </StyledReviewSection>
      )}

      {canAccessScreeningCriteriaInListingFlow === false && (
        <StyledReviewSection data-testid="Pets">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder>
                {!active && isPetsAllowedEmpty && !isDisabled ? (
                  <AccordionIconError tabIndex={0} title="Pets incomplete error" marginRight="xs" />
                ) : (
                  <>
                    {recommendations[RENT_DETAILS][RECS.PETS_NOT_ALLOWED] && (
                      <RecommendationIconBulb
                        fieldName={ariaLabelDisplayPets}
                        removeFlex={true}
                        onClick={() => {
                          setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                        }}
                      />
                    )}
                  </>
                )}
              </StyledIconPlaceholder>
              <StyledReviewSectionTitle>
                <Text textStyle="body-bold">{displayPetsHeading}</Text>
                <TextButton
                  aria-label={`learn-more-about-${ariaLabelDisplayPets}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.PETS_NOT_ALLOWED);
                  }}
                >
                  Learn more
                </TextButton>
              </StyledReviewSectionTitle>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabelDisplayPets}
              value={MODAL_COMPONENT.PetPolicyFormField.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={displayPetsAvailable} customNoneText={customNoneFieldValueText} />
        </StyledReviewSection>
      )}

      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

RentDetailsContent.propTypes = propTypes;
RentDetailsContent.defaultProps = defaultProps;

export default RentDetailsContent;
